import { yellow } from '@material-ui/core/colors';
import { isNumber } from 'lodash';
import { IconType, getIcon } from 'src/modules/data';
import { CountryCode, ValuationCountryCode } from 'src/modules/generated/api';
import { getLabel, promotions } from 'src/modules/labels';
import { Tax } from 'src/types/tax';

export const getTaxId = (country: ValuationCountryCode, tax: Tax) => `taxes.${country}.${tax}`;

type HighlightPromotion = {
  key: string;
  promotion: string;
  hiddenForDe?: boolean;
  highlighted?: boolean;
};

// Highlights only show this whitelist of promotions, in this fixed order — unlike the dedicated
// promotions column, which renders every promotion the car carries.
const HIGHLIGHT_PROMOTIONS: HighlightPromotion[] = [
  { key: 'fokus', promotion: 'FOKUS_MODELL' },
  { key: 'bonus', promotion: 'BONUS_MODELL', hiddenForDe: true },
  { key: 'vvj_promo', promotion: 'PHS_VVJ', hiddenForDe: true },
  { key: 'faktura2027', promotion: 'FAKTURA2027' },
  { key: 'gwturbo', promotion: 'GWTURBO' },
  { key: 'EXPORT_promo', promotion: 'EXPORT', hiddenForDe: true },
  { key: 'heissw', promotion: 'HEISSW_AKTION' },
  { key: 'aftershow', promotion: 'AFTERSHOW', highlighted: true },
  { key: 'stellplatz', promotion: 'STELLPLATZ', highlighted: true },
];

/**
 * Resolves the promotions a car shows as highlights. Shared by the highlights column and the car
 * detail page, which used to keep their own lists and drifted apart.
 */
export const filterHighlightPromotions = (
  promotionKeys: string[] | undefined,
  dealerCountry: CountryCode | undefined,
) =>
  HIGHLIGHT_PROMOTIONS.filter(
    ({ promotion, hiddenForDe }) =>
      promotionKeys?.includes(promotion) && !(hiddenForDe && dealerCountry === CountryCode.De),
  );

/**
 * Builds the promotion avatars for the highlights column.
 *
 * Package rows aggregate promotions across their cars, so they must resolve against the same
 * whitelist as single car rows — otherwise a promotion shows up on the package but not on the car
 * it came from.
 */
export const buildPromotionAvatars = (promotionKeys: string[] | undefined, dealerCountry: CountryCode | undefined) =>
  filterHighlightPromotions(promotionKeys, dealerCountry).map(({ key, promotion, highlighted }) => ({
    key,
    src: getIcon(IconType.PROMOTION, promotion),
    helpText: getLabel(promotions, promotion),
    style: {
      background: highlighted ? yellow[200] : undefined,
    },
  }));

// untangle the different percentage formats (floats 0-1; integers 0-100) in order to properly compare values
// necessary for conditional formats
export const getRelativeIntValue = (value: number | undefined): number | undefined =>
  isNumber(value) ? value * 100 : value;
